import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, Truck, CheckCircle2, Wallet, IndianRupee, ArrowRight, Gift } from "lucide-react";
import { appApi, inr, fmtDate } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";

const ACTIVE = ["created", "manifested", "pending_assignment", "pickup_scheduled", "in-transit", "in_transit", "out-for-delivery", "out_for_delivery", "ndr"];

export default function Overview() {
  const [data, setData] = useState(null);

  useEffect(() => {
    Promise.allSettled([
      appApi.get("wallet"),
      appApi.get("orders"),
      appApi.get("cod/summary"),
      appApi.get("wallet/first-recharge-offer"),
    ]).then(([w, o, c, offer]) => {
      setData({
        wallet: w.status === "fulfilled" ? w.value : null,
        orders: o.status === "fulfilled" ? o.value.orders || [] : [],
        cod: c.status === "fulfilled" ? c.value : null,
        offer: offer.status === "fulfilled" ? offer.value : null,
      });
    });
  }, []);

  if (!data)
    return <p className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-400" data-testid="overview-loading">Loading overview…</p>;

  const orders = data.orders;
  const kpis = [
    { label: "Wallet Balance", value: data.wallet ? inr(data.wallet.wallet.balance_inr) : "—", icon: Wallet, testid: "kpi-wallet" },
    { label: "Total Orders", value: orders.length, icon: Package, testid: "kpi-total-orders" },
    { label: "Active Shipments", value: orders.filter((o) => ACTIVE.includes(String(o.status).toLowerCase())).length, icon: Truck, testid: "kpi-active" },
    { label: "Delivered", value: orders.filter((o) => String(o.status).toLowerCase() === "delivered").length, icon: CheckCircle2, testid: "kpi-delivered" },
    { label: "COD Pending", value: data.cod ? inr(data.cod.total_pending_inr) : "—", icon: IndianRupee, testid: "kpi-cod-pending" },
  ];

  return (
    <div data-testid="overview-page" className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs font-mono font-bold uppercase tracking-[0.2em] text-blaze">Overview</p>
          <h1 className="font-display font-black tracking-tight text-3xl sm:text-4xl mt-1">Command Deck</h1>
        </div>
        <Link
          to="/dashboard/rates"
          data-testid="overview-rates-cta"
          className="inline-flex items-center gap-2 bg-acid text-white text-sm font-bold px-5 py-3 border-2 border-obsidian shadow-[4px_4px_0px_0px_#0A0A0A] hover:shadow-none hover:translate-x-[4px] hover:translate-y-[4px] transition-all duration-150"
        >
          Check Rates <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      {data.offer?.eligible && (
        <div data-testid="first-recharge-banner" className="border-2 border-obsidian bg-acid px-5 py-4 flex items-center gap-3 font-semibold text-sm text-white">
          <Gift className="w-5 h-5" />
          First recharge offer: top up {inr(data.offer.min_recharge_inr)}+ and get {data.offer.cashback_pct}% cashback.
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-5 border-2 border-obsidian bg-white">
        {kpis.map((k, i) => (
          <div key={k.testid} data-testid={k.testid} className={`p-5 border-obsidian ${i !== 0 ? "lg:border-l-2" : ""} border-t-2 lg:border-t-0 first:border-t-0 lg:first:border-t-0 ${i % 2 === 1 ? "border-l-2 lg:border-l-2" : ""}`}>
            <div className="flex items-center justify-between">
              <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">{k.label}</p>
              <k.icon className="w-4 h-4 text-zinc-400" />
            </div>
            <p className="font-display font-black text-2xl sm:text-3xl tracking-tight mt-2">{k.value}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="status-strip">
        {[
          { k: "in-transit", l: "In Transit" },
          { k: "out-for-delivery", l: "Out for Delivery" },
          { k: "ndr", l: "NDR" },
          { k: "delivered", l: "Delivered" },
        ].map((s) => (
          <Link key={s.k} to={`/dashboard/orders?status=${s.k}`} data-testid={`status-chip-${s.k}`}
            className="border-2 border-obsidian bg-white px-5 py-4 hover:shadow-[4px_4px_0px_0px_#0B1E3A] hover:-translate-y-0.5 transition-all duration-150">
            <p className="text-[10px] font-mono font-bold uppercase tracking-widest text-zinc-500">{s.l}</p>
            <p className="font-display font-black text-2xl tracking-tight mt-1">
              {orders.filter((o) => String(o.status).toLowerCase() === s.k).length}
            </p>
          </Link>
        ))}
      </div>

      <div className="border-2 border-obsidian bg-white">
        <div className="flex items-center justify-between px-5 py-4 border-b-2 border-obsidian">
          <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">Recent orders</p>
          <Link to="/dashboard/orders" data-testid="overview-view-all-orders" className="text-xs font-bold text-blaze hover:underline">View all</Link>
        </div>
        <table className="w-full text-sm" data-testid="recent-orders-table">
          <tbody>
            {orders.slice(0, 6).map((o) => (
              <tr key={o.id || o.awb} className="border-b border-zinc-100 last:border-0 hover:bg-acid/20 transition-colors duration-100">
                <td className="px-5 py-3">
                  <p className="font-mono text-xs font-bold">{o.awb}</p>
                  <p className="text-xs text-zinc-500">{o.courier_name} · {o.shipment_type?.toUpperCase()}</p>
                </td>
                <td className="px-2 py-3 text-xs text-zinc-500 font-mono hidden sm:table-cell">
                  {o.pickup?.city} → {o.delivery?.city}
                </td>
                <td className="px-2 py-3 text-xs font-mono hidden md:table-cell">{fmtDate(o.created_at)}</td>
                <td className="px-5 py-3 text-right"><StatusBadge status={o.status} /></td>
              </tr>
            ))}
            {orders.length === 0 && (
              <tr><td className="px-5 py-8 text-sm text-zinc-500">No orders yet — book your first shipment from the app or check rates here.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
